/**
 * React Query hooks for saved filter presets.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

import type {
  CreateSavedFilterPayload,
  UpdateSavedFilterPayload,
  ShareFilterPayload,
  UnshareFilterPayload,
} from '@/features/smswiser-campaigns/types/saved-filter.types';
import { mapSavedFilterFromApi } from '@/features/smswiser-campaigns/utils/mapSavedFilterFromApi';

const KEYS = {
  all: ['saved-stats-filters'] as const,
  byTab: (tab: string) => ['saved-stats-filters', tab] as const,
};

const BASE = '/saved-stats-filters';

export function useSavedFilters(tab: string) {
  return useQuery({
    queryKey: KEYS.byTab(tab),
    queryFn: async () => {
      const res = await api.get<{ data: unknown[] }>(`${BASE}?tab=${tab}`);
      return res.data.data.map(mapSavedFilterFromApi);
    },
  });
}

interface ShareableUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export function useShareableUsers() {
  return useQuery({
    queryKey: ['saved-stats-filters', 'shareable-users'] as const,
    queryFn: async () => {
      const res = await api.get<{ data: ShareableUser[] }>(`${BASE}/shareable-users`);
      return res.data.data;
    },
    staleTime: 60_000,
  });
}

export function useSaveFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateSavedFilterPayload) => {
      const res = await api.post<{ data: unknown }>(BASE, payload);
      return mapSavedFilterFromApi(res.data.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateSavedFilterPayload) => {
      const res = await api.patch<{ data: unknown }>(`${BASE}/${id}`, patch);
      return mapSavedFilterFromApi(res.data.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${BASE}/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useShareFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ filterId, userIds }: ShareFilterPayload) => {
      await api.post(`${BASE}/${filterId}/share`, { userIds });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUnshareFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ filterId, userId }: UnshareFilterPayload) => {
      await api.delete(`${BASE}/${filterId}/share/${userId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}
